import React, { forwardRef } from 'react'
import { Field, Form, Formik } from 'formik'
import {
  Box,
  Typography,
  Card,
  Divider,
  Icon,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { format, getYear, parse } from 'date-fns'

// Local Import
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxSwitch from 'components/reusable/switch/AdxSwitch'
import CustomHolidayForm from 'module/leave/pages/editOperationDays/components/CustomHolidayForm'
import EditCustomHolidayForm from 'module/leave/pages/editOperationDays/components/EditCustomHolidayForm'
import DeleteCustomModal from 'module/leave/pages/editOperationDays/components/DeleteCustomModal'

// API services
import {
  useGetHoliday,
  useUpdatePublicHoliday,
  usePostNewCustomHoliday,
} from 'services/api/modules/leave/holiday'
import AdxButton from 'components/custom/AdxButton'

const HolidaysConfiguration = forwardRef((props, ref) => {
  const { data: holidaysData, isLoading } = useGetHoliday()

  const mutation = usePostNewCustomHoliday()
  const mutationUpdate = useUpdatePublicHoliday()

  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('lg'))

  const updatePublicHoliday = () => {
    mutationUpdate.mutate()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ gap: 1 }}
      >
        <Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ gap: 1 }}>
            <Typography variant="h3" color="dark-text.main">
              Holidays
            </Typography>
          </Box>
          <Typography variant="h4" color="light-gray-text.main">
            Manage public holidays or add custom holidays for your organization
          </Typography>
        </Box>
        <AdxStatusBar
          status={{
            text: `Year ${getYear(new Date())}`,
            bgColor: 'light-information.main',
            color: 'information.main',
          }}
        />
      </Box>
      {isLoading ? (
        <Box sx={{ width: '955px', height: '415px' }}>Loading...</Box>
      ) : (
        <>
          <Box sx={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              <Typography sx={{ width: 300 }} variant="h4" color="dark-text.main">
                Public Holiday
              </Typography>
              <Typography sx={{ width: 1 }} variant="h4" color="light-gray-text.main">
                Public holidays are automatically added here. Enable/Disable accordingly
              </Typography>
            </Box>

            {/* Formik Starts Here */}
            <Formik
              innerRef={ref}
              enableReinitialize
              initialValues={{
                publicHoliday: holidaysData,
              }}
              onSubmit={(values) => {
                if (values.publicHoliday) {
                  const data = values.publicHoliday.public_holiday.map((val) => ({
                    ...val,
                    date: format(parse(val.date, 'dd/MM/yyyy', new Date()), `yyyy-MM-dd`),
                  }))

                  mutation.mutate(
                    { holiday: data },
                    {
                      onSuccess: () => {
                        props.setButtonLoading((prevState) => ({
                          ...prevState,
                          holiday: false,
                        }))
                        props.setCanNavigate((prevState) => ({
                          ...prevState,
                          holiday: true,
                        }))
                      },
                    }
                  )
                } else {
                  props.setButtonLoading((prevState) => ({
                    ...prevState,
                    holiday: false,
                  }))
                  props.setCanNavigate((prevState) => ({
                    ...prevState,
                    holiday: true,
                  }))
                }
              }}
            >
              {({ values, handleBlur, setFieldValue }) => {
                return (
                  <Form>
                    <>
                      <Card
                        variant="outlined"
                        sx={{
                          boxSizing: 'border-box',
                          borderRadius: '10px',
                        }}
                      >
                        <Box display={'flex'} flexDirection={'column'}>
                          {values.publicHoliday ? (
                            values?.publicHoliday?.public_holiday?.map((item, index) => {
                              return (
                                <Box key={item.id}>
                                  {/* Grid method */}
                                  {matches ? (
                                    <>
                                      <Box
                                        display={'flex'}
                                        flexDirection={'row'}
                                        alignItems={'center'}
                                        p={2}
                                      >
                                        <Box
                                          width={1 / 6}
                                          display={'flex'}
                                          flexDirection={'column'}
                                        >
                                          <Typography
                                            color="light-gray-text.main"
                                            variant="subtitle1"
                                          >
                                            {item.day}
                                          </Typography>
                                          <Typography
                                            color="light-gray-text.main"
                                            variant="subtitle1"
                                          >
                                            {item.date}
                                          </Typography>
                                        </Box>
                                        <Box width={1 / 4} mr={4}>
                                          <Typography variant="body1" color="dark-text.main">
                                            {item.name}
                                          </Typography>
                                        </Box>
                                        <Box width={2 / 4}>
                                          <Typography variant="body1" color="dark-text.main">
                                            {item.location}
                                          </Typography>
                                        </Box>
                                        <Box
                                          display={'flex'}
                                          width={1 / 4}
                                          justifyContent={'flex-end'}
                                        >
                                          <Field
                                            name={`publicHoliday.public_holiday[${index}].status`}
                                            as={AdxSwitch}
                                            state={
                                              values.publicHoliday.public_holiday[index].status ===
                                              1
                                                ? true
                                                : false
                                            }
                                            label1="Active"
                                            label2="Inactive"
                                            onChange={(e) => {
                                              setFieldValue(
                                                `publicHoliday.public_holiday[${index}].status`,
                                                e.target.checked ? 1 : 2
                                              )
                                            }}
                                            onBlur={handleBlur}
                                          />
                                        </Box>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      <Box
                                        display={'flex'}
                                        flexDirection={'row'}
                                        alignItems={'center'}
                                        p={2}
                                      >
                                        <Box
                                          display={'flex'}
                                          flexDirection={'column'}
                                          width={3 / 4}
                                        >
                                          <Box display={'flex'} flexDirection={'column'}>
                                            <Typography
                                              color="light-gray-text.main"
                                              variant="subtitle1"
                                            >
                                              {item.day}
                                            </Typography>
                                            <Typography
                                              color="light-gray-text.main"
                                              variant="subtitle1"
                                            >
                                              {item.date}
                                            </Typography>
                                          </Box>
                                          <Box width={4 / 5}>
                                            <Typography variant="body1" color="dark-text.main">
                                              {item.name}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box
                                          display={'flex'}
                                          width={1 / 4}
                                          justifyContent={'flex-end'}
                                        >
                                          <Field
                                            name={`publicHoliday.public_holiday[${index}].status`}
                                            as={AdxSwitch}
                                            state={
                                              values.publicHoliday.public_holiday[index].status ===
                                              1
                                                ? true
                                                : false
                                            }
                                            onChange={(e) => {
                                              setFieldValue(
                                                `publicHoliday.public_holiday[${index}].status`,
                                                e.target.checked ? 1 : 2
                                              )
                                            }}
                                            onBlur={handleBlur}
                                          />
                                        </Box>
                                      </Box>
                                    </>
                                  )}

                                  <Divider sx={{ width: '100%' }} light />
                                </Box>
                              )
                            })
                          ) : (
                            <>
                              <Box
                                sx={{
                                  width: '955px',
                                  height: '50px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                No data...
                              </Box>
                              <Divider sx={{ width: '100%' }} light />
                            </>
                          )}
                        </Box>
                        <Box sx={{ boxSizing: 'border-box', width: '100%' }}>
                          <AdxButton variant={'ghost'} sx={{ width: '100%' }}>
                            <Box width={1} display={'flex'} alignItems={'center'}>
                              <Icon
                                sx={{
                                  marginRight: 1,
                                  color: 'alternate.main',
                                }}
                              >
                                update
                              </Icon>
                              <Box
                                width={1}
                                display={'flex'}
                                flexDirection={{ xs: 'column', sm: 'row' }}
                                onClick={updatePublicHoliday}
                              >
                                <Typography
                                  mr={{ xs: null, sm: 1 }}
                                  sx={{
                                    fontWeight: 'bold',
                                  }}
                                  variant="subtitle1"
                                  color={'alternate.main'}
                                >
                                  Update Public Holiday
                                </Typography>
                                {mutationUpdate.isLoading ? (
                                  <CircularProgress size="1rem" />
                                ) : (
                                  <Typography
                                    pt={0.2}
                                    sx={{
                                      fontWeight: 'bold',
                                      fontStyle: holidaysData && 'italic',
                                    }}
                                    variant="caption"
                                    color={'light-gray-text.main'}
                                  >
                                    {holidaysData
                                      ? `Last updated on ${holidaysData?.updated_at}`
                                      : 'Never'}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </AdxButton>
                        </Box>
                      </Card>
                    </>
                  </Form>
                )
              }}
            </Formik>
            {/* Formik Ends Here */}
          </Box>
          <Box sx={{ boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h4" color="dark-text.main">
                Custom Holiday
              </Typography>
              <Typography variant="h4" color="light-gray-text.main">
                Create and manage custom holidays
              </Typography>
            </Box>

            <Card
              variant="outlined"
              sx={{
                mb: 3,
                boxSizing: 'border-box',
                borderRadius: '10px',
                overflow: 'auto',
              }}
            >
              <Box display={'flex'} flexDirection={'column'}>
                {!holidaysData?.custom_holiday && (
                  <>
                    <Box
                      sx={{
                        width: '935px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      No data...
                    </Box>
                    <Divider sx={{ width: '100%' }} light />
                  </>
                )}
                {holidaysData?.custom_holiday?.map((item, index) => {
                  return (
                    <React.Fragment key={item.id}>
                      {matches ? (
                        <>
                          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} p={2}>
                            <Box width={1 / 7} display={'flex'} flexDirection={'column'}>
                              <Typography color="light-gray-text.main" variant="subtitle1">
                                {item.day}
                              </Typography>
                              <Typography color="light-gray-text.main" variant="subtitle1">
                                {item.date}
                              </Typography>
                            </Box>
                            <Box width={5 / 7}>
                              <Typography width={150} variant="body1" color="dark-text.main">
                                {item.name}
                              </Typography>
                            </Box>
                            <Box
                              width={1 / 3}
                              display={'flex'}
                              flexDirection={'row'}
                              justifyContent={'flex-end'}
                              gap={1}
                            >
                              <DeleteCustomModal state={item} />
                              <EditCustomHolidayForm state={item} />
                            </Box>
                          </Box>
                          <Divider sx={{ width: '100%' }} light />
                        </>
                      ) : (
                        <>
                          <Box
                            display={'flex'}
                            flexDirection={'column'}
                            alignItems={'center'}
                            p={2}
                          >
                            <Box mb={1} display={'flex'} flexDirection={'row'} width={1}>
                              <Box
                                width={1 / 2}
                                display={'flex'}
                                flexDirection={'column'}
                                justifyContent={'center'}
                              >
                                <Typography color="light-gray-text.main" variant="subtitle1">
                                  {item.day}
                                </Typography>
                                <Typography color="light-gray-text.main" variant="subtitle1">
                                  {item.date}
                                </Typography>
                              </Box>
                              <Box width={1 / 2} display={'flex'} justifyContent={'center'}>
                                <Typography width={150} variant="body1" color="dark-text.main">
                                  {item.name}
                                </Typography>
                              </Box>
                            </Box>

                            <Box
                              width={1 / 3}
                              display={'flex'}
                              flexDirection={'column'}
                              justifyContent={'flex-start'}
                            >
                              <EditCustomHolidayForm state={item} />
                              <DeleteCustomModal state={item} />
                            </Box>
                          </Box>
                          <Divider sx={{ width: '100%' }} light />
                        </>
                      )}
                    </React.Fragment>
                  )
                })}
              </Box>
              <CustomHolidayForm />
            </Card>
          </Box>
        </>
      )}
    </Box>
  )
})

export default HolidaysConfiguration
